import { getTextRenderMetrics } from '../labelTextFit';
import { loadPhomymoPrinter, loadPhomymoRenderer, loadPhomymoTransport } from './phomymoLoader';

// This module only maps Klaro data and orchestrates original Phomymo APIs.
// It contains no GATT discovery, write strategy, command encoder or rasterizer.
let sessionTail = Promise.resolve();
const number = (value, fallback = 0) => Number.isFinite(Number(value)) ? Number(value) : fallback;
const report = (callback, message) => { if (callback) callback(message); };
export const isPhomymoProfile = profileId => ['phomemo_m220', 'phomemo_m221'].includes(profileId);

async function inSession(action) {
  const previous = sessionTail;
  let release;
  sessionTail = new Promise(resolve => { release = resolve; });
  await previous;
  try { return await action(); }
  finally { release(); }
}

async function sharedTransport() {
  const { BLETransport } = await loadPhomymoTransport();
  const transport = BLETransport.getShared();
  transport.onDisconnect = () => window.dispatchEvent(new CustomEvent('klaro-printer-binding-changed'));
  return transport;
}

export async function requestPhomymoPrinter({ profiles, onProgress = null }) {
  return inSession(async () => {
    const transport = await sharedTransport();
    // This function is called only by an explicit Associa/Riassocia action.
    // End the old association here, never at the end of a print job.
    if (transport.device) await transport.disconnect();
    report(onProgress, 'Seleziona la stampante Phomemo…');
    await transport.connect();
    if (!transport.isConnected()) throw new Error('Connessione Phomemo non riuscita');
    const name = transport.getDeviceName() || '';
    const { getMatchedPattern } = await loadPhomymoPrinter();
    const detectedModel = String(getMatchedPattern(name) || '').toLowerCase();
    const detectedId = detectedModel === 'm220' || detectedModel === 'm221' ? `phomemo_${detectedModel}` : null;
    const profile = detectedId ? profiles.find(item => item.id === detectedId) : profiles[0];
    if (!profile || (detectedModel && !detectedId)) {
      await transport.disconnect();
      throw new Error('La stampante selezionata non corrisponde al profilo Phomemo configurato');
    }
    report(onProgress, `Phomemo collegata · ${name}`);
    return {
      device: transport.device, name, profileId: profile.id, profile,
      detectedByName: detectedId,
      capabilities: {
        protocol: profile.id, dpi: [203], mediaPresets: [],
        mediaDetected: false, mediaDetection: 'manual_fallback',
      },
    };
  });
}

export async function getPhomymoAvailability(binding) {
  const transport = await sharedTransport();
  const connected = transport.device?.id === binding.deviceId && transport.isConnected();
  // A saved association is selectable after reload. A fresh picker, when
  // needed, is opened by connectDirectPrinter from the user's Print click.
  return { available: true, bound: true, connected,
    deviceName: connected ? transport.getDeviceName() : binding.deviceName,
    label: connected ? 'Collegata su questo dispositivo' : 'Da collegare alla stampa' };
}

async function connectBinding(binding, onProgress) {
  if (!binding?.deviceId || !isPhomymoProfile(binding.profileId)) {
    throw new Error('Associa prima la stampante Phomemo in Opzioni → Stampanti');
  }
  const transport = await sharedTransport();
  if (transport.device && transport.device.id !== binding.deviceId) await transport.disconnect();
  report(onProgress, 'Collegamento Phomemo…');
  await transport.connect();
  if (!transport.isConnected()) throw new Error('Connessione Phomemo non riuscita');
  if (transport.device.id !== binding.deviceId) {
    await transport.disconnect();
    throw new Error('Hai selezionato una stampante diversa. Seleziona quella associata oppure usa Riassocia in Opzioni.');
  }
  const { getMatchedPattern } = await loadPhomymoPrinter();
  const detectedModel = String(getMatchedPattern(transport.getDeviceName()) || '').toLowerCase();
  if (detectedModel && `phomemo_${detectedModel}` !== binding.profileId) {
    throw new Error('Il modello della stampante non corrisponde al profilo associato. Usa Riassocia in Opzioni.');
  }
  return transport;
}

export function ensurePhomymoConnection(binding, onProgress = null) {
  return inSession(() => connectBinding(binding, onProgress));
}

export function forgetPhomymoDevice(deviceId) {
  return inSession(async () => {
    const transport = await sharedTransport();
    if (transport.device?.id === deviceId) await transport.disconnect();
  });
}

async function loadImage(source) {
  return new Promise((resolve, reject) => {
    const image = new Image();
    const timer = window.setTimeout(() => reject(new Error('Caricamento immagine etichetta scaduto')), 15000);
    const finish = fn => { window.clearTimeout(timer); fn(); };
    image.onload = () => finish(() => resolve(image));
    image.onerror = () => finish(() => reject(new Error('Immagine etichetta non caricabile')));
    if (!String(source).startsWith('data:')) image.crossOrigin = 'anonymous';
    image.src = source;
  });
}

// Convert mm/points and resolved fields into native Phomymo elements. QR
// content, label version and template remain owned by Klaro.
export async function preparePhomymoLabel(renderSource, printerModel) {
  const { CanvasRenderer, PX_PER_MM, getPrinterWidthBytes, getPrinterDpi, getPrinterAlignment } = await loadPhomymoRenderer();
  const layout = renderSource?.layout;
  if (!layout?.elements) throw new Error('Template etichetta non disponibile');
  const widthMm = number(renderSource.paper_width_mm, number(layout.width_mm, 60));
  const heightMm = number(renderSource.paper_height_mm, number(layout.height_mm, 40));
  const widthBytes = getPrinterWidthBytes('', printerModel);
  if (widthMm <= 0 || Math.round(widthMm * PX_PER_MM) > widthBytes * 8 || heightMm <= 0 || heightMm * PX_PER_MM > 1200) {
    throw new Error('Formato etichetta non compatibile con Phomemo M220/M221');
  }
  const margin = Math.max(0, number(renderSource.margin_mm));
  const horizontalOffsetMm = number(renderSource.horizontal_offset_mm);
  if (horizontalOffsetMm < -10 || horizontalOffsetMm > 10) {
    throw new Error('Correzione orizzontale Phomemo fuori intervallo (-10…10 mm)');
  }
  if (number(layout.width_mm) + 2 * margin > widthMm + 0.01 || number(layout.height_mm) + 2 * margin > heightMm + 0.01) {
    throw new Error('Il supporto selezionato è più piccolo dell’etichetta e dei margini');
  }
  if (document.fonts?.ready) await document.fonts.ready;
  const canvas = document.createElement('canvas');
  const renderer = new CanvasRenderer(canvas);
  renderer.setDimensions(widthMm, heightMm, 1);
  try {
    const elements = [];
    for (const [index, element] of layout.elements.entries()) {
      const x = (number(element.x_mm) + margin) * PX_PER_MM;
      const y = (number(element.y_mm) + margin) * PX_PER_MM;
      const width = Math.max(1, number(element.w_mm, 20) * PX_PER_MM);
      const height = Math.max(1, number(element.h_mm, 3) * PX_PER_MM);
      const base = { id: `klaro-${index}`, x, y, width, height, rotation: 0 };
      if (element.type === 'qr') {
        const qrData = String(renderSource.data?.[element.field || 'qr_payload'] ?? '');
        if (!qrData) continue;
        const size = Math.min(width, height);
        const qrCanvas = document.createElement('canvas');
        await new Promise((resolve, reject) => window.QRCode.toCanvas(qrCanvas, qrData, {
          width: size, margin: 1, color: { dark: '#000000', light: '#ffffff' },
        }, error => error ? reject(error) : resolve()));
        renderer._addToRenderCache(`qr_${base.id}_${qrData}_${size}`, qrCanvas);
        elements.push({ ...base, type: 'qr', qrData });
      } else if (element.type === 'image') {
        if (!element.content) continue;
        const image = await loadImage(element.content);
        const ratio = Math.min(width / image.naturalWidth, height / image.naturalHeight);
        const imageWidth = image.naturalWidth * ratio;
        const imageHeight = image.naturalHeight * ratio;
        renderer._addToImageCache(base.id, image);
        elements.push({ ...base, type: 'image', imageData: image.src,
          x: x + (width - imageWidth) / 2, y: y + (height - imageHeight) / 2,
          width: imageWidth, height: imageHeight });
      } else if (element.type === 'line' || element.type === 'rectangle') {
        const strokeWidth = Math.max(1, number(element.stroke_width, 0.25) * PX_PER_MM);
        elements.push({ ...base, type: 'shape', shapeType: element.type,
          fill: 'transparent', stroke: 'black', strokeWidth,
          ...(element.type === 'rectangle' ? {
            x: x + strokeWidth / 2, y: y + strokeWidth / 2,
            width: Math.max(1, width - strokeWidth), height: Math.max(1, height - strokeWidth),
          } : { y: y + strokeWidth / 2, height: 0 }),
        });
      } else if (element.type === 'static' || element.type === 'dynamic') {
        const text = String(element.type === 'dynamic' ? (renderSource.data?.[element.field] ?? '') : (element.content ?? ''));
        const metrics = getTextRenderMetrics(element, text, number(layout.width_mm, 60));
        // Upstream has 4px text padding on each side. Compensate in the
        // mapping so existing Klaro text anchors stay at the same positions.
        elements.push({ ...base, type: 'text', text, x: x - 4, width: metrics.widthMm * PX_PER_MM + 8,
          fontSize: metrics.fontSizePt * 203 / 72, fontFamily: 'Arial, Helvetica, sans-serif',
          fontWeight: element.bold ? 'bold' : 'normal', fontStyle: 'normal',
          align: element.align || 'left', verticalAlign: 'middle', color: 'black',
          noWrap: true, clipOverflow: true, autoScale: false });
      } else {
        throw new Error(`Elemento etichetta non supportato: ${element.type}`);
      }
    }
    // Apply calibration to every element before rasterization so text, borders,
    // images and QR move together. Positive moves right; negative moves left.
    const offsetPixels = horizontalOffsetMm * PX_PER_MM;
    if (offsetPixels) elements.forEach(element => { element.x += offsetPixels; });
    const raster = renderer.getRasterData(elements, widthBytes, getPrinterDpi('', printerModel), 'auto', getPrinterAlignment('', printerModel));
    if (raster.data.length !== raster.widthBytes * raster.heightLines) throw new Error('Raster Phomymo incompleto');
    return { raster, widthMm, heightMm };
  } finally { renderer.destroy(); }
}

export function sendPhomymoLabel({ binding, renderSource, copies = 1, onProgress = null }) {
  return inSession(async () => {
    const transport = await connectBinding(binding, onProgress);
    const printerModel = binding.profileId.replace('phomemo_', '');
    report(onProgress, 'Preparazione etichetta Phomymo…');
    const { raster } = await preparePhomymoLabel(renderSource, printerModel);
    const { print } = await loadPhomymoRenderer();
    const count = Math.max(1, Math.min(500, Math.floor(number(copies, 1))));
    const density = Math.max(1, Math.min(8, Math.round(number(renderSource.density, 6))));
    const feed = Math.max(0, Math.min(255, Math.round(number(renderSource.feed_dots, 32))));
    let bytesWritten = 0;
    let writeAttempted = false;
    const originalSend = transport.send;
    // Observe the original sender for Klaro's job result; never replace its
    // strategy, buffer handling, packet sizes or timing.
    transport.send = async function (data) {
      writeAttempted = true;
      await originalSend.call(this, data);
      bytesWritten += data.byteLength;
    };
    try {
      for (let copy = 1; copy <= count; copy += 1) {
        await print(transport, raster, {
          isBLE: true, deviceName: transport.getDeviceName(), printerModel, density, feed,
          onProgress: percent => report(onProgress, `Invio Phomymo · copia ${copy}/${count} · ${percent}%`),
        });
        // This is the copy-loop delay in upstream app.js handlePrint().
        if (copy < count) await transport.delay(500);
      }
      return { bytesWritten, copies: count, widthBytes: raster.widthBytes, heightLines: raster.heightLines };
    } catch (error) {
      error.bytesWritten = bytesWritten;
      error.writeAttempted = writeAttempted;
      throw error;
    } finally { transport.send = originalSend; }
  });
}

export function createPhomymoTestSource({ widthMm = 70, heightMm = 50, density = 6 } = {}) {
  const qrSize = Math.max(0, Math.min(16, widthMm - 6, heightMm - 20));
  return {
    layout: {
      width_mm: widthMm, height_mm: heightMm,
      elements: [
        { type: 'rectangle', x_mm: 1.5, y_mm: 1.5, w_mm: widthMm - 3, h_mm: heightMm - 3, stroke_width: 0.35 },
        { type: 'static', content: 'KLARO · TEST STAMPA', x_mm: 3, y_mm: 3, w_mm: widthMm - 6, h_mm: 4, font_size: 8, bold: true, align: 'center' },
        { type: 'static', content: 'Qualità, caffè, perché, già', x_mm: 3, y_mm: 8, w_mm: widthMm - 6, h_mm: 3, font_size: 5, align: 'center' },
        { type: 'static', content: `Densità ${density}/8 · 203 dpi`, x_mm: 3, y_mm: 12, w_mm: widthMm - 6, h_mm: 3, font_size: 4.5, align: 'center' },
        ...(qrSize >= 6 ? [{ type: 'qr', field: 'qr_payload', x_mm: (widthMm - qrSize) / 2, y_mm: heightMm - qrSize - 3, w_mm: qrSize, h_mm: qrSize }] : []),
      ],
    },
    data: { qr_payload: 'KLARO-PHOMEMO-TEST-2026' },
    copies: 1, density, feed_dots: 32,
  };
}
